import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLocationPathname } from '@rocketlane/rocket-router';
import React, { Suspense, useMemo } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { PageLoading } from 'components/atoms/loading';
import { AgentGlobalResetStyles, AgentGlobalStyles } from 'components/styles/components/agent-global-styles';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useAgentBuilderHostAPI } from 'hooks/use-agent-builder-host-api';
import { useIsKnowledgeBaseFeatureEnabled } from 'hooks/use-knowledge-base-feature';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404';
const AgentBuilderApp = /*#__PURE__*/ React.lazy(()=>import('agentBuilder/AgentBuilderApp').then((module)=>({
            default: module.AgentBuilderApp
        })));
const AgentBuilderWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${AgentGlobalResetStyles};
`;
export const AgentBuilderRemote = ()=>{
    var _ref, _ref1;
    var _accountData_accountMeta;
    const pathname = useLocationPathname();
    const { hasAccessToResource } = useWithAccess();
    const { data: accountData } = useAccountDataQuery();
    const features = accountData === null || accountData === void 0 ? void 0 : (_accountData_accountMeta = accountData.accountMeta) === null || _accountData_accountMeta === void 0 ? void 0 : _accountData_accountMeta.features;
    const isAiChatEnabled = (_ref = features === null || features === void 0 ? void 0 : features.aiChat) !== null && _ref !== void 0 ? _ref : false;
    const isScarletEnabled = (_ref1 = features === null || features === void 0 ? void 0 : features.scarlet) !== null && _ref1 !== void 0 ? _ref1 : false;
    const isKnowledgeBaseEnabled = useIsKnowledgeBaseFeatureEnabled();
    const [isProjectGovernancePoliciesEnabled] = useAccountFeature(AccountFeature.PolicyEvaluation);
    const [isTimesheetPoliciesEnabled] = useAccountFeature(AccountFeature.TimesheetPolicies);
    const [isMigrationAgentFeatureEnabled] = useAccountFeature(AccountFeature.MigrationAgent);
    const [isWorkforceAgentFeatureEnabled] = useAccountFeature(AccountFeature.WorkforceAgent);
    const hasFeatures = useMemo(()=>({
            documentationAgent: features === null || features === void 0 ? void 0 : features.documentationAgent,
            scarlet: features === null || features === void 0 ? void 0 : features.scarlet,
            knowledgeBase: isKnowledgeBaseEnabled,
            migrationAgent: isMigrationAgentFeatureEnabled,
            workforceAgent: isWorkforceAgentFeatureEnabled,
            nitroAnalyst: features === null || features === void 0 ? void 0 : features.nitroAnalyst,
            signalsAnalyst: features === null || features === void 0 ? void 0 : features.signalsAnalyst,
            timesheetPolicies: features === null || features === void 0 ? void 0 : features.timesheetPolicies,
            policyEvaluation: features === null || features === void 0 ? void 0 : features.policyEvaluation,
            aiChat: isAiChatEnabled
        }), [
        features === null || features === void 0 ? void 0 : features.documentationAgent,
        features === null || features === void 0 ? void 0 : features.scarlet,
        isKnowledgeBaseEnabled,
        features === null || features === void 0 ? void 0 : features.nitroAnalyst,
        features === null || features === void 0 ? void 0 : features.signalsAnalyst,
        features === null || features === void 0 ? void 0 : features.timesheetPolicies,
        features === null || features === void 0 ? void 0 : features.policyEvaluation,
        isMigrationAgentFeatureEnabled,
        isWorkforceAgentFeatureEnabled,
        isAiChatEnabled
    ]);
    const permissions = useMemo(()=>({
            documentAgent: {
                read: hasAccessToResource({
                    rule: 'DOCUMENT_AGENT:READ'
                }),
                write: hasAccessToResource({
                    rule: 'DOCUMENT_AGENT:WRITE'
                }),
                delete: hasAccessToResource({
                    rule: 'DOCUMENT_AGENT:DELETE'
                }),
                create: hasAccessToResource({
                    rule: 'DOCUMENT_AGENT:CREATE'
                }),
                invoke: hasAccessToResource({
                    rule: 'DOCUMENT_AGENT:CREATE'
                })
            },
            meetingPageTemplates: {
                read: hasAccessToResource({
                    rule: 'MEETING_PAGE_TEMPLATE:READ'
                }),
                write: hasAccessToResource({
                    rule: 'MEETING_PAGE_TEMPLATE:WRITE'
                }),
                delete: hasAccessToResource({
                    rule: 'MEETING_PAGE_TEMPLATE:DELETE'
                }),
                create: hasAccessToResource({
                    rule: 'MEETING_PAGE_TEMPLATE:CREATE'
                })
            },
            emailPageTemplates: {
                read: hasAccessToResource({
                    rule: 'EMAIL_PAGE_TEMPLATE:READ'
                }),
                write: hasAccessToResource({
                    rule: 'EMAIL_PAGE_TEMPLATE:WRITE'
                }),
                delete: hasAccessToResource({
                    rule: 'EMAIL_PAGE_TEMPLATE:DELETE'
                }),
                create: hasAccessToResource({
                    rule: 'EMAIL_PAGE_TEMPLATE:CREATE'
                })
            },
            nitroAnalyst: {
                create: isAiChatEnabled && hasAccessToResource({
                    rule: 'AI_ANALYST:CREATE'
                })
            },
            signalsAnalyst: {
                create: hasAccessToResource({
                    rule: 'SIGNAL_ANALYST:CREATE'
                })
            }
        }), [
        hasAccessToResource,
        isAiChatEnabled
    ]);
    const agentBuilderSubPaths = [
        '/create',
        '/edit',
        '/livepreview',
        '/experts/',
        '/migration-agents/'
    ];
    const excludedAgentBuilderSubPaths = [
        '/migration-agents/create'
    ];
    const isAgentBuilderPath = pathname.startsWith('/agent-builder') && agentBuilderSubPaths.some((subPath)=>pathname.includes(subPath)) && !excludedAgentBuilderSubPaths.some((subPath)=>pathname.includes(subPath));
    const hostAPI = useAgentBuilderHostAPI();
    // Block access if user doesn't have permission
    // Requires both documentationAgent feature and DOCUMENT_AGENT:READ (when Scarlet is not enabled)
    const canAccessDocumentAgent = hasFeatures.documentationAgent && permissions.documentAgent.read;
    const canAccessMigrationAgent = isMigrationAgentFeatureEnabled && hasAccessToResource({
        rule: 'MIGRATION_AGENT:READ'
    });
    const canAccessWorkforceAgent = isWorkforceAgentFeatureEnabled && hasAccessToResource({
        rule: 'WORKFORCE_AGENT:READ'
    });
    const isProjectGovernancePoliciesVisible = isProjectGovernancePoliciesEnabled && hasAccessToResource({
        rule: 'PROJECT_GOVERNANCE_POLICIES:CREATE'
    });
    const isTimesheetPoliciesVisible = isTimesheetPoliciesEnabled && hasAccessToResource({
        rule: 'TIMESHEET_POLICIES:CREATE'
    });
    if (!isScarletEnabled && !canAccessDocumentAgent && !canAccessMigrationAgent && !canAccessWorkforceAgent && !isProjectGovernancePoliciesVisible && !isTimesheetPoliciesVisible) {
        return /*#__PURE__*/ _jsx(NotFound, {});
    }
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
        children: /*#__PURE__*/ _jsxs(StyleSheetManager, {
            target: document.head,
            children: [
                /*#__PURE__*/ _jsx(AgentGlobalStyles, {}),
                /*#__PURE__*/ _jsx(AgentBuilderWrapper, {
                    className: "cp-pt-2 cp-pb-2 cp-pl-0 cp-pr-2",
                    $isAgentBuilderPath: isAgentBuilderPath,
                    children: /*#__PURE__*/ _jsx(AgentBuilderApp, {
                        hostAPI: hostAPI,
                        permissions: permissions,
                        hasFeatures: hasFeatures
                    })
                })
            ]
        })
    });
};
